// src/components/templates/templateSamples.js
//
// The sample writing each template's "Preview Sample" shows, keyed by template
// id and then by section index. `src/utils/templateSample.js` puts it under the
// template's own section headings and explains the block shapes.
//
// **Transcribed from the gallery prototype**, the same scenes and characters it
// used, so a reader comparing two templates is comparing structures rather than
// writing. What changed is only how it is set: the prototype drew screenplays,
// comic scripts and papers in their industry layouts, and the editor does not
// lay those out yet. Here every line is what an author typing that script into
// the app today would get — ordinary paragraphs. When a format is built, its
// sample changes with it.
//
// The prototype's worksheet pages (beat sheets, maps, trackers, thumbnail
// grids) are not here: nothing in the app makes them. The planning sheets'
// filled-in fields are, as the labelled lines an author would type.
//
// Trusted static HTML: entities are written as entities (`&amp;`).

const SEAWALL_CH1 = [
  'The tide came in early that year, ahead of the almanac and ahead of anyone’s memory of it. Maya Chen stood at the drafting-room window and watched the water climb the seawall one gray step at a time, the way it had every morning since October.',
  'She had built that wall. Not alone, and not well enough, but her name was on the plans pinned to the corkboard behind her, and the town of Harrow Point still believed in it the way people believe in anything they have stopped looking at.',
  '“It’s holding,” said Dev from the doorway. He had brought two coffees and the expression he wore when he wanted her to agree with him.',
  '“It’s holding today,” she said.',
  'Below them, a gull lifted from the breakwater and hung motionless in the wind, as if it, too, were waiting to see what the water would decide.',
];

const TEMPLATE_SAMPLES = {
  'tpl-three-act-novel': {
    0: SEAWALL_CH1,
  },

  'tpl-heros-journey': {
    0: [
      'The valley of Oakhaven felt smaller every morning. Jon Ashby noticed it first in the fences, which seemed to lean closer to the road, and then in the mountains, which no longer looked like a horizon so much as a wall.',
      'He fed the goats before dawn, as he always did. He mended the gate his father had promised to mend for eleven years. He told himself, as he always did, that a quiet life was a kind of courage.',
      'Then the raven came.',
      'It landed on the frost-covered gatepost and regarded him with one black, deliberate eye. Around its leg, bound in red thread, was a scroll sealed in iron—the seal of the High Keep, which had not sent word to Oakhaven in living memory.',
      'Jon did not reach for it. Not at first. He stood in the cold with the bucket still in his hand and understood, the way you understand weather, that his quiet days were finished.',
    ],
  },

  'tpl-short-story': {
    0: [
      'She kept the key in a brass box under floorboard three, right beside the dried lavender from the summer they almost drowned in the quarry. Her brother never asked what it opened. That was the arrangement: he didn’t ask, and she didn’t tell him that she didn’t know either.',
      'It had come in an envelope with no return address the week their mother died, wrapped in a page torn from a hymnal.',
    ],
    1: [
      'The verse was circled in pencil. She had looked it up, of course, the way she looked up everything, and found that it was a hymn about rivers—about crossing them, mostly, and about what waits on the other side.',
      'For eleven years she let the key sleep. She married and unmarried. She moved twice and took the floorboard with her both times, which her second landlord found funnier than her first.',
      'Then, in March, her brother called from the old house.',
      '“There’s a door in the cellar,” he said. “I think it’s been there the whole time.”',
      { center: '#' },
      'The cellar smelled of lime and wet stone. The door was smaller than she expected, painted the same gray as the wall, its keyhole furred with rust.',
      '“Well?” her brother said.',
      'She held the key in her palm until it was warm. Then she understood—not what was behind the door, but why their mother had sent it to her and not to him.',
    ],
  },

  'tpl-poetry-collection': {
    0: [
      { h2: 'Inventory at Low Tide' },
      { lines: ['What the water leaves, it leaves', 'on purpose: one blue bottle cap,', 'the jaw of something small,', 'a rope that has forgotten', 'what it held.'] },
      { lines: ['My mother walked this line', 'each April, counting.', 'She said the sea keeps books', 'the way we don’t—', 'everything entered, nothing owed.'] },
      { lines: ['I count now. I come back', 'with my pockets full of the ordinary', 'and my hands', 'smelling of salt and iron,'] },
      { lines: ['like a key, like a stone,', 'like the shape of silence', 'after someone says your name.'] },
    ],
  },

  'tpl-memoir': {
    0: [
      { h2: 'Chapter One: Pell Street' },
      'The roof of the house on Pell Street leaked whenever it rained hard from the east, and in the spring of 1994 it rained hard from the east for nineteen days. My mother set out every pot we owned. My father, who believed that storms have memories of their own, climbed the ladder each evening to look at the sky as though it owed him an explanation.',
      'I was nine. I thought the dripping was music. I learned to fall asleep to its uneven rhythm—the saucepan’s tin bell, the stockpot’s low knock, the soft, unbearable patience of the bucket by the door.',
      'It would be twenty years before I understood that a house can hold a family together and still let the weather in, and that the two are not as different as they sound.',
    ],
  },

  'tpl-selfhelp': {
    1: [
      { h2: 'Chapter One: The One Action' },
      'Transformation is not an event of willpower. It is an architecture of small, compounding decisions, built in quiet rooms when no one is watching.',
      'Most of us were taught the opposite. We were told that change arrives in a blaze—a New Year’s resolution, a dramatic purge of the pantry, a 5 a.m. alarm set with heroic intent. For a week, perhaps two, the blaze holds. Then ordinary life returns, and the fire goes out.',
      'Radical clarity begins with a simpler question: What is the one action that, done consistently, makes every other action easier?',
      '<b>KEY IDEA</b> — Clarity is subtraction. Before you add a habit, remove the three things that make it hard.',
    ],
  },

  'tpl-business-book': {
    0: [
      'In 2019, a mid-sized logistics firm in Ohio had more data than at any point in its sixty-year history. It tracked every pallet, every mile, every idle minute at every dock. Its dashboards glowed. Its quarterly reviews ran four hours long.',
      'It was also losing money on nearly a third of its routes, and no one in the building could explain why.',
      'This book is about that paradox. In an economy flooded with commodity data, information is no longer the scarce resource. Clarity is. The organizations that win the next decade will not be the ones that measure the most—they will be the ones that decide fastest about what matters.',
    ],
  },

  'tpl-feature-screenplay': {
    0: [
      'FADE IN:',
      'EXT. HARROW POINT SEAWALL - DAWN',
      'Gray water slaps a concrete wall stained with a century of high-water marks. Gulls wheel and scream overhead.',
      'MAYA CHEN (34), wind-burned, hood up, walks the top of the wall with a tape measure and a dented thermos.',
      'She stops. Kneels. Runs a gloved finger along a crack that wasn’t there yesterday.',
      'MAYA',
      '(under her breath)',
      'Oh, you have got to be kidding me.',
      'EXT. MAIN STREET - CONTINUOUS',
      'A fishing town shrugs awake. A bakery flips its sign to OPEN. DEV OKAFOR (40s), easy smile, jogs to catch up, two coffees in hand.',
      'DEV',
      'You slept here again.',
      'MAYA',
      'I slept near here.',
      'DEV',
      'Council meets at nine. Tell me you’re not bringing the crack.',
      'Maya takes a coffee. Doesn’t answer. Dev follows her gaze back to the wall.',
      'DEV (CONT’D)',
      'Maya.',
      'CUT TO:',
      'INT. HARROW POINT COFFEE SHOP - DAY',
      'Crowded. Steamed windows. Maya enters, scanning the room until she finds a corner table with a view of the water.',
      'She sits. Pulls a worn leather folder from her coat. Inside: the original seawall plans, annotated in three colors of ink.',
      'The BARISTA (20s) sets down a muffin she didn’t order.',
      'BARISTA',
      'From the guy by the door.',
      'Maya looks up. The door swings shut. No one there.',
      'Under the muffin, a plain white ENVELOPE. No stamp. No return address. Just her name, in handwriting she hasn’t seen in fifteen years.',
      'MAYA',
      '(barely audible)',
      'Dad?',
      'She tears it open. A single KEY falls onto the plans, landing squarely on the weakest section of the wall.',
    ],
  },

  'tpl-halfhour-pilot': {
    0: [
      'INT. BRIGHTLINE MEDIA - OFFICE KITCHEN - MORNING',
      'A toaster smokes like a small industrial accident. MARK DELGADO (30s), tie already loosened at 8:52 a.m., stares at it as if it personally insulted his family.',
      'SARAH (O.S.)',
      'Is that another bagel sacrificed to the gods of procrastination?',
      'SARAH PATEL (30s), coffee in one hand, laptop in the other, leans in the doorway.',
      'MARK',
      'It’s a protest bagel. Management moved the toaster to the “collaboration zone.”',
      'SARAH',
      'That’s a hallway.',
      'MARK',
      'That’s a hallway with a beanbag.',
      'The smoke alarm SHRIEKS. Every head in the open-plan office swivels toward them.',
      'MARK (CONT’D)',
      '(to the room)',
      'Team-building exercise! Nobody panic!',
      'END OF COLD OPEN',
    ],
    1: [
      'INT. BRIGHTLINE MEDIA - CONFERENCE ROOM - LATER',
      'A whiteboard reads SYNERGY SUMMIT in three colors. JUNE OKAMOTO (50s), the boss, taps a laser pointer against her palm.',
      'JUNE',
      'Someone set off the fire alarm with a bagel. I’m not asking who.',
      'Everyone looks at Mark.',
      'JUNE (CONT’D)',
      'But whoever it was is now running the Summit.',
      'MARK',
      'That seems like a punishment.',
      'JUNE',
      'It’s a growth opportunity.',
      'SARAH',
      '(whispering)',
      'Same thing here.',
    ],
  },

  'tpl-hourlong-pilot': {
    0: [
      'INT. 14TH PRECINCT - INTERROGATION ROOM - NIGHT',
      'Fluorescent light. A steel table bolted to the floor. The overhead microphone hums with electric interference.',
      'DETECTIVE RUTH VANCE (50s), reading glasses pushed into gray hair, slides a photograph across the table.',
      'Across from her, ELI MARSH (20s), hoodie, bloodshot eyes, refuses to look down.',
      'VANCE',
      'You know her.',
      'ELI',
      'I know a lot of people.',
      'VANCE',
      'She knew you. She wrote your name on her hand the night she disappeared.',
      'Eli finally looks. His jaw tightens.',
      'ELI',
      '(quiet)',
      'That’s not my name.',
      'The lights FLICKER. Somewhere down the hall, a phone begins to ring and does not stop.',
      'SMASH CUT TO:',
      'MAIN TITLES',
      'END OF TEASER',
    ],
  },

  'tpl-fulllength-play': {
    0: [
      '(The parlor. Late afternoon. A grandfather clock ticks upstage. Rain against tall windows. ELEANOR paces center, clutching a leather-bound journal. Distant thunder.)',
      'ELEANOR',
      'They think the past is buried. They have never dug in wet clay.',
      '(CLARA enters with a tea tray. She sets it down without a sound.)',
      'CLARA',
      'Mr. Thomas’s train was delayed, ma’am. The station sent a boy.',
      'ELEANOR',
      'Delayed. Of course it was. The river is up, and everything that floats is coming home.',
      'CLARA',
      'Shall I lay a place for him?',
      'ELEANOR',
      '(Beat.)',
      'Lay two.',
      'CLARA',
      'Two, ma’am?',
      'ELEANOR',
      'Dr. Pike will want to see what the rain brings in. He always does.',
      '(The clock strikes five. ELEANOR opens the journal, reads, and closes it quickly as the front door BANGS open offstage.)',
      'THOMAS',
      '(Offstage.)',
      'Mother? Is anyone in this house still awake?',
      '(Lights shift.)',
    ],
  },

  'tpl-oneact-play': {
    0: [
      '(Darkness. The hum of machinery dies. A soft emergency light flickers on, revealing ARTHUR and BEATRICE in a stalled elevator. BEATRICE jabs the emergency button.)',
      'ARTHUR',
      'If you press that button one more time, I swear the cables will snap.',
      'BEATRICE',
      'Let them. At least down there I won’t have to listen to your opinions.',
      'ARTHUR',
      'It isn’t an opinion. It’s physics.',
      'BEATRICE',
      '(Pressing it again.)',
      'This is physics.',
      '(The elevator lurches. Both grab the rail. Silence.)',
      'ARTHUR',
      '...You were saying.',
      'BEATRICE',
      'I was saying I have a gallery opening in forty minutes and a seven-foot bronze stuck in a freight lift.',
      'ARTHUR',
      'And I have a filing deadline at midnight and a woman pressing buttons like they owe her money.',
      '(A long beat. BEATRICE slides down the wall and sits. After a moment, ARTHUR, carefully, sits too.)',
      'BEATRICE',
      'Do you always count things when you’re nervous?',
      'ARTHUR',
      'I wasn’t counting.',
      'BEATRICE',
      'You were at forty-three.',
    ],
  },

  'tpl-comic-issue': {
    0: [
      'PAGE ONE (One Panel)',
      'PANEL 1 — SPLASH',
      'Establishing shot of Neo-London at dusk, drowned in perpetual amber fog. Neon spires pierce the haze. In the lower third, a tiny figure — MAYA — stands at the edge of a rooftop, coat whipping in the wind.',
      '1  CAPTION: Neo-London. Forty years after the tide came in and never left.',
      '2  CAPTION: They say the fog remembers everything.',
      '3  TITLE: AMBER FOG',
    ],
    1: [
      'PAGE TWO (Five Panels)',
      'PANEL 1',
      'Close on Maya’s eyes, reflecting pink neon. Rain beads on her lashes.',
      '1  MAYA (CAPTION): The weight of memory comes back in colors. Rust. Neon.',
      'PANEL 2',
      'Wide. A police DRONE rises behind her, searchlight blazing.',
      '2  SFX: VRRRMMMM',
      '3  DRONE (electronic): CITIZEN. REMAIN WHERE YOU ARE.',
      'PANEL 3',
      'Maya turns, already running.',
      '4  MAYA: Not tonight.',
      'PANEL 4',
      'She LEAPS the gap between rooftops — small figure, enormous drop. No lettering.',
      'PANEL 5',
      'She lands hard, rolls. The drone’s light sweeps the empty roof behind her.',
      '5  SFX: THUD',
    ],
  },

  'tpl-graphic-novel': {
    1: [
      'CHAPTER THREE — PAGES 42–43 (Double-Page Spread, One Panel)',
      'PANEL 1 — FULL BLEED SPREAD',
      'The colossal skyship CALLISTO pierces the storm clouds above the jagged canyon. Lightning forks across its brass hull. Tiny on the foredeck: our heroes, NELL and the old pilot ODEN, braced against the rail. Below, the canyon town we spent two chapters in is the size of a coin.',
      'Artist note: this is the moment the book opens up. Let the sky take two-thirds of the spread.',
      '1  CAPTION: Nell had imagined the sky her whole life.',
      '2  CAPTION: She had imagined it too small.',
      '3  SFX: KRRAKOOOM',
      'PAGE 44 (Four Panels)',
      'PANEL 1',
      'Nell, soaked, laughing, hair plastered to her face.',
      '1  NELL: We’re above it! Oden, we’re above the storm!',
      'PANEL 2',
      'Oden doesn’t share her joy. He’s looking at a compass spinning wildly.',
      '2  ODEN: We’re above a storm.',
      'PANEL 3',
      'Close on the compass needle snapping north.',
      '3  SFX: tik',
      'PANEL 4',
      'Wide. Ahead of them, a wall of black cloud the size of a mountain range.',
      '4  ODEN (small): Not that one.',
    ],
  },

  'tpl-mla-paper': {
    0: [
      { lines: ['Maya Chen', 'Professor Alvarez', 'English 201', '14 October 2026'] },
      { center: 'The Weight of Memory: Tides and Time in Coastal Fiction' },
    ],
    1: [
      'Coastal fiction returns again and again to the tide as a figure for memory, a force that erases and preserves at once (Smith 42). In novels set along shorelines, the rhythm of the water marks not only the passage of days but the return of what characters have tried to forget. This essay argues that the tide functions less as setting than as structure: it organizes narrative time into cycles of loss and recovery, and it asks readers to consider whether memory, like a coastline, is ever truly fixed.',
    ],
    2: [
      'Critics have long noted the shoreline’s in-between status. Rivera describes the beach as ground that belongs fully to neither land nor sea (17), and Okafor extends this reading to argue that coastal narratives borrow the tide’s logic of erosion and return (91). Yet neither account fully explains why so many of these novels end where they begin, at the waterline, with a character watching the tide come in.',
      'The answer, I suggest, lies in the way these books handle repetition. In Asdah’s reading, the tide is “a clock that runs in circles” (41), and the circularity is the point: characters do not escape their pasts so much as learn to read them differently each time the water recedes.',
      'Consider the opening of a typical shoreline novel. The protagonist stands at a window, watching the water climb a seawall she built. The scene is ordinary, even static, but the tide introduces a pressure the prose never names directly. By the final chapter, the same image recurs with the meaning reversed. The wall has failed; the water has come in; and the character, who once measured the tide in order to control it, now simply watches.',
    ],
    3: [
      'Some readers resist this interpretation, arguing that the tide is merely atmospheric—an easy shorthand for melancholy (Smith 58). That objection has force. But it overlooks how deliberately these novels align their structural turns with the tide tables their characters consult.',
    ],
    5: [
      'Asdah, James. <i>Tidal Forces: Memory and the Maritime Imagination</i>. Calloway Press, 2020.',
      'Okafor, Nneka. “Erosion and Return: Narrative Time on the Shoreline.” <i>Journal of Coastal Literature</i>, vol. 14, no. 2, 2022, pp. 88–104.',
      'Rivera, Lucia. <i>The Liminal Shore: Beaches in the Modern Novel</i>. Northfield UP, 2018.',
      'Smith, Helena. <i>Writing the Sea: Essays on Place and Form</i>. Harbor House, 2019.',
    ],
  },

  'tpl-apa-paper': {
    0: [
      { center: '<b>Tides as Narrative Structure: Memory and Recurrence in Contemporary Coastal Fiction</b>' },
      { center: 'Maya Chen<br>Department of English, Harbor State University<br>ENG 201: Literature and Place<br>Dr. Lucia Alvarez<br>October 14, 2026' },
      { center: '<b>Author Note</b>' },
      'Correspondence concerning this article should be addressed to Maya Chen, Department of English, Harbor State University. Email: mchen@harborstate.edu',
    ],
    1: [
      'This study examines how contemporary coastal fiction uses tidal imagery to structure narrative time. A corpus of 24 novels published between 2000 and 2024 was coded for recurring tidal scenes, their position within each text, and their association with acts of remembering. Tidal scenes clustered at structural turning points (<i>M</i> = 3.4 per novel, <i>SD</i> = 1.1) and were significantly more likely than other weather imagery to co-occur with flashback. Results suggest that the tide functions as a structural device rather than as atmosphere alone. Implications for narrative theory and the study of place in fiction are discussed.',
      '<i>Keywords:</i> coastal fiction, memory, narrative time, recurrence, setting',
    ],
    3: [
      { h2: 'Corpus' },
      'The corpus comprised 24 novels set primarily on a coastline and published in English between 2000 and 2024. Selection followed the criteria used in prior work on literary setting (Rivera &amp; Park, 2021), excluding novels in which the coast appeared in fewer than three chapters.',
      { h2: 'Coding Procedure' },
      'Two trained coders independently identified every scene containing tidal imagery and recorded its chapter position as a percentage of total length. Interrater agreement was high (κ = .87). Disagreements were resolved through discussion.',
      'Each tidal scene was also coded for co-occurrence with flashback or explicit remembering, following the operational definition proposed by Okafor (2022).',
    ],
    4: [
      'Tidal scenes appeared in all 24 novels and clustered near structural turning points (Smith, 2020).',
    ],
    5: [
      'Asdah, J. (2020). <i>Tidal forces: Memory and the maritime imagination</i>. Calloway Press.',
      'Okafor, N. (2022). Erosion and return: Narrative time on the shoreline. <i>Journal of Coastal Literature, 14</i>(2), 88–104. https://doi.org/10.5555/jcl.2022.0142',
      'Rivera, L., &amp; Park, S. (2021). Measuring setting: A coding framework for place in the novel. <i>Narrative Studies Quarterly, 9</i>(1), 22–41. https://doi.org/10.5555/nsq.2021.0091',
      'Smith, H. (2020). <i>Writing the sea: Essays on place and form</i> (2nd ed.). Harbor House.',
    ],
  },

  'tpl-chicago-paper': {
    0: [
      { center: 'The Seawall Years: Memory, Engineering, and Civic Identity<br>in Harrow Point, 1912–1980' },
      { center: 'Maya Chen<br>HIST 340: Coastal Communities<br>October 14, 2026' },
    ],
    2: [
      'When the Harrow Point seawall was first poured in the spring of 1912, the town council described it as a promise kept to future generations.<sup>1</sup> Few residents at the time could have imagined how often that promise would be renegotiated. Over the following seven decades the wall was raised, patched, and partially rebuilt no fewer than six times, and each reconstruction left a trace not only in concrete but in the town’s sense of itself.',
      'Coastal historians have tended to read such structures as purely technical achievements.<sup>2</sup> Yet the archival record suggests that the seawall functioned as a civic text, one that residents read, argued over, and rewrote. The concrete mixture failed during the winter storms of 1944, a failure later traced to unwashed aggregate,<sup>3</sup> and the public debate that followed reveals far more about local politics than about engineering.',
      '1. Harrow Point Town Council, <i>Minutes of the Regular Meeting</i>, April 3, 1912, Harrow Point Historical Society, box 4, folder 2.',
      '2. James Asdah, <i>Tidal Forces: Memory and the Maritime Imagination</i> (Calloway Press, 2020), 41.',
      '3. Asdah, <i>Tidal Forces</i>, 77.',
    ],
    5: [
      'Asdah, James. <i>Tidal Forces: Memory and the Maritime Imagination</i>. Calloway Press, 2020.',
      'Harrow Point Town Council. <i>Minutes of the Regular Meeting</i>, April 3, 1912. Harrow Point Historical Society, box 4, folder 2.',
      'Okafor, Nneka. “Erosion and Return: Narrative Time on the Shoreline.” <i>Journal of Coastal Literature</i> 14, no. 2 (2022): 88–104.',
      'Whitcombe, Arthur. <i>Concrete Against the Sea: Municipal Engineering in New England, 1900–1960</i>. Northfield University Press, 2015.',
    ],
  },

  'tpl-character-sheet': {
    0: [
      '<b>Name:</b> Maya Chen',
      '<b>Role:</b> Protagonist',
      '<b>Age:</b> 34',
      '<b>Occupation:</b> Marine architect',
      '<b>Appearance:</b> Wind-burned; ink on her cuffs; her father’s oilskin coat',
      '<b>Voice:</b> Dry, precise; deflects with facts',
      '<b>Home:</b> Above the drafting office, Wharf St.',
    ],
    1: [
      '<b>Want:</b> Rebuild the seawall before the equinox',
      '<b>Need:</b> Let other people carry some of the weight',
      '<b>Flaw:</b> Pathological self-reliance',
      '<b>Secret fear:</b> The wall failed because she cut corners',
    ],
    2: [
      '<b>Age 9:</b> Father leaves after the 2001 storm; she keeps his coat.',
      '<b>Age 22:</b> First seawall design rejected publicly by the council.',
      '<b>Age 31:</b> Builds the current wall on a cut budget — and signs off.',
    ],
    3: [
      '<b>Dev Okafor</b> — Colleague / friend. Steady, protective. He wants her to rest.',
      '<b>Tom Chen</b> — Estranged father. Silence, then letters. Abandonment.',
      '<b>Mayor Hale</b> — Antagonist. Polite, political. Tourism vs. safety.',
      '<b>Ivy Chen</b> — Younger sister. Teasing, close. Ivy wants her to leave town.',
    ],
    4: [
      '<b>Starts believing:</b> “If I don’t hold it together, no one will.”',
      '<b>Ends realizing:</b> “Holding on alone is how things break.”',
    ],
  },

  'tpl-location-sheet': {
    0: [
      '<b>Name:</b> The Cliffside Lighthouse',
      '<b>Type:</b> Working lighthouse, decommissioned 1979',
      '<b>Region:</b> Northern headland, Harrow Point',
      '<b>Climate:</b> Persistent sea mist; gale-force winter winds',
      '<b>Terrain:</b> Granite cliff, 60 m drop to shingle',
      '<b>Architecture:</b> Whitewashed brick tower, iron lantern room',
      '<b>Story role:</b> Where Maya finds her father’s letters',
    ],
    1: [
      '<b>Sight:</b> Beam sweeping fog every 12 s',
      '<b>Sound:</b> Iron bell tolling every 60 s',
      '<b>Smell:</b> Kerosene, kelp, wet stone',
      '<b>Touch:</b> Salt-crusted railings, cold brass',
      '<b>Taste:</b> Salt on the lips',
      '<b>Winter:</b> Horizontal rain; the stairs ice over by 4 p.m.',
      '<b>Summer:</b> Thick morning fog that burns off by noon.',
    ],
    2: [
      '<b>1871</b> — Tower built after the wreck of the <i>Margaret Ann</i>',
      '<b>1912</b> — Seawall poured; lighthouse road paved',
      '<b>1944</b> — Keeper’s logbook goes missing in the winter storms',
      '<b>1979</b> — Light automated; keeper’s cottage abandoned',
    ],
    3: [
      '<b>Lantern room</b> — Iron gallery, cracked prism glass. Ch. 4, 17',
      '<b>Keeper’s cottage</b> — Two rooms, roof half-collapsed. Ch. 9',
      '<b>The Steps</b> — 112 iron stairs spiralling the tower. Ch. 4, 22',
      '<b>Bell house</b> — Fog bell on a rusted frame. Ch. 12',
      '<b>Secret:</b> A loose brick in the lantern room hides the 1944 logbook — and Tom Chen’s letters.',
    ],
  },

  'tpl-research-library': {
    0: [
      '<b>Town Council Minutes, 1912</b> — Harrow Point Hist. Soc., box 4. Tags: seawall, civic. Status: Read',
      '<b>Interview: R. Gale (keeper’s granddaughter)</b> — Recorded 3 Mar 2026. Tags: lighthouse. Status: Transcribed',
    ],
    1: [
      '<b><i>Tidal Forces</i> — J. Asdah</b> — Calloway Press, 2020. Tags: memory, tide. Status: Notes',
      '<b>Okafor, “Erosion and Return”</b> — <i>J. Coastal Lit.</i> 14.2. Tags: narrative time. Status: To read',
      '<b><i>Concrete Against the Sea</i> — Whitcombe</b> — Northfield UP, 2015. Tags: engineering. Status: Notes',
      '<b>Key finding:</b> Concrete mixture failed during the winter storms of ’44 due to unwashed aggregate. → Use in Ch. 9 flashback.',
    ],
    2: [
      '<b>Seawall blueprints 1912–1980</b> — City engineer archive. Tags: engineering. Status: Scanned',
      'Blueprint, 1912 · Lighthouse, 1950s photo · Storm damage, 1944',
    ],
    3: [
      '“A promise kept to future generations.” — Town Council Minutes, 3 Apr 1912, p. 2 (Ch. 1 epigraph)',
      'The tide is a clock that runs in circles. — Asdah, <i>Tidal Forces</i>, 41 (Theme · paraphrase)',
      '“Grandad said the bell was for the ships, but really it was for him.” — R. Gale interview, 00:14:32 (Ch. 12 dialogue)',
    ],
  },
};

export default TEMPLATE_SAMPLES;
